import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";
import { AuditTrailForExport, getAuditTrailData } from "../util/getAuditTrailData";

const baseUrl = BASE_URL + "sugg-rule";

// 📌 Create Suggestion Rule (POST)
export const createSuggestionRule = createAsyncThunk(
  "suggestion/create",
  async (ruleData, { rejectWithValue }) => {
    try {
      // Ensure audit trail present just like other modules
      const audit_trail_data =
        ruleData?.audit_trail_data ||
        (await getAuditTrailData({
          action: "CREATE_SUGGESTION_RULE",
          comment: `Created suggestion rule: ${ruleData?.rule_name || ""}`,
          version_content: ruleData?.version_content || {},
        }));

      const payload = { ...ruleData, audit_trail_data };
      const response = await axios.post(`${baseUrl}/create`, payload);

      // Check if response indicates failure (success: false)
      if (response.data && response.data.success === false) {
        return rejectWithValue(response.data);
      }

      return response.data;
    } catch (error) {
      // Check if error response has success: false
      if (error.response?.data && error.response.data.success === false) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(
        error.response?.data || "Error creating suggestion rule"
      );
    }
  }
);

// 📌 Update Suggestion Rule (PUT)
export const updateSuggestionRule = createAsyncThunk(
  "suggestion/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const audit_trail_data =
        data?.audit_trail_data ||
        (await getAuditTrailData({
          action: "UPDATE_SUGGESTION_RULE",
          comment: `Updated suggestion rule: ${data?.rule_name || ""}`,
          parent_version: data?.version_id_ref,
          version_content: data?.version_content || {},
        }));
      const payload = { ...data, audit_trail_data };
      const response = await axios.put(`${baseUrl}/update/${id}`, payload);

      // Check if response indicates failure (success: false)
      if (response.data && response.data.success === false) {
        return rejectWithValue(
          response.data.message || response.data.error || "Operation failed"
        );
      }

      return response.data;
    } catch (error) {
      // Check if error response has success: false
      if (error.response?.data && error.response.data.success === false) {
        return rejectWithValue(
          error.response.data.message ||
            error.response.data.error ||
            "Operation failed"
        );
      }
      return rejectWithValue(
        error.response?.data || "Error updating suggestion rule"
      );
    }
  }
);

// 📌 Get Suggestion Rule by ID (GET)
export const getSuggestionRuleById = createAsyncThunk(
  "suggestion/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/get-by-id/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching suggestion rule"
      );
    }
  }
);

// 📌 Get Criteria Formats (GET)
export const getCriteriaFormats = createAsyncThunk(
  "suggestion/getCriteriaFormats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/get-criteria`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching criteria formats"
      );
    }
  }
);

// 📌 Get All Suggestion Rules (GET)
export const getAllSuggestionRules = createAsyncThunk(
  "suggestion/getAll",
  async (
    {
      searchQuery = "",
      limit = 10,
      offset = 0,
      searchType = "rule_name,criteria,type_name,criteria_format_name",
    } = {},
    { rejectWithValue }
  ) => {
    try {
      let url = `${baseUrl}/get-all`;
      const params = new URLSearchParams();
      
      // If searchQuery is a full query string, use it directly
      if (searchQuery && searchQuery.includes("=")) {
        const s = new URLSearchParams(searchQuery);
        for (let [k, v] of s.entries()) {
          params.set(k, v); // Use set instead of append to avoid duplicates
        }
      } else {
        // If searchQuery is just a search term, build params from separate parameters
        if (searchQuery) {
          params.append("search", searchQuery);
        }
        params.append("limit", limit);
        params.append("offset", offset);
        if (searchType) {
          params.append("search_type", searchType);
        }
      }
      
      const qs = params.toString();
      if (qs) url += `?${qs}`;
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching suggestion rules"
      );
    }
  }
);

// 📌 Delete Suggestion Rule (DELETE)
export const deleteSuggestionRule = createAsyncThunk(
  "suggestion/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${baseUrl}/delete/${id}`);

      // Check if response indicates failure (success: false)
      if (response.data && response.data.success === false) {
        return rejectWithValue(
          response.data.message || response.data.error || "Operation failed"
        );
      }

      return id;
    } catch (error) {
      // Check if error response has success: false
      if (error.response?.data && error.response.data.success === false) {
        return rejectWithValue(
          error.response.data.message ||
            error.response.data.error ||
            "Operation failed"
        );
      }
      return rejectWithValue(
        error.response?.data || "Error deleting suggestion rule"
      );
    }
  }
);

// 📌 Export CSV (POST)
export const exportSuggestionRulesCSV = createAsyncThunk(
  "suggestion/exportCSV",
  async (_, { rejectWithValue }) => {
    try {
      const file_name =  `export-rule-${Date.now()}.csv`;
      const ad = await getAuditTrailData({
        action: "EXPORT_Audit_data",
        comment: "-",
        version_content: {
          file_name,
          action_to_show: "Suggestion Rules data exported as CSV",
        },
      });
      const response = await axios.post(
        `${baseUrl}/export`,
        {
          responseType: "blob",
          file_name,
          audit_trail_data: ad,
        },
        {
          responseType: "blob",
        }
      );

      // Download the file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", file_name);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error exporting suggestion rules CSV"
      );
    }
  }
);

// 📌 Upload CSV (POST)
export const uploadSuggestionRulesCSV = createAsyncThunk(
  "suggestion/uploadCSV",
  async ({ csvFile, auditTrailData }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", csvFile);

      // Ensure audit_trail_data is properly formatted as JSON string
      const auditData =
        auditTrailData ||
        (await getAuditTrailData({
          action: "IMPORT_SUGGESTION_RULES_DATA",
          comment: "Imported suggestion rules via CSV",
        }));

      formData.append("audit_trail_data", JSON.stringify(auditData));

      const response = await axios.post(`${baseUrl}/import`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Check if response indicates failure (success: false)
      if (response.data && response.data.success === false) {
        return rejectWithValue(response.data);
      }

      return response.data;
    } catch (error) {
      // Check if error response has success: false
      if (error.response?.data && error.response.data.success === false) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(
        error.response?.data || "Error uploading suggestion rules CSV"
      );
    }
  }
);

// 🛠 Suggestion Slice
const suggestionSlice = createSlice({
  name: "suggestion",
  initialState: {
    suggestions: [],
    suggestion: null,
    criteriaFormats: [],
    loading: false,
    error: null,
    pagination: { limit: 10, offset: 0, total: 0 },
  },
  reducers: {
    setLimit: (state, action) => {
      state.pagination.limit = action.payload;
    },
    setOffset: (state, action) => {
      state.pagination.offset = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createSuggestionRule.pending, (state) => {
        state.loading = true;
      })
      .addCase(createSuggestionRule.fulfilled, (state, action) => {
        state.loading = false;
        state.suggestions.push(action.payload);
      })
      .addCase(createSuggestionRule.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload.message ||
          action.payload.success ||
          action.payload.error ||
          "An Error has occurred";
      })
      .addCase(updateSuggestionRule.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateSuggestionRule.fulfilled, (state, action) => {
        state.loading = false;
        state.suggestions = state.suggestions.map((rule) =>
          rule.id === action.payload.id ? action.payload : rule
        );
      })
      .addCase(updateSuggestionRule.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload.message ||
          action.payload.success ||
          action.payload.error ||
          "An Error has occurred";
      })
      .addCase(getSuggestionRuleById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSuggestionRuleById.fulfilled, (state, action) => {
        state.loading = false;
        state.suggestion = action.payload;
      })
      .addCase(getSuggestionRuleById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload.message ||
          action.payload.success ||
          action.payload.error ||
          "An Error has occurred";
      })
      .addCase(getAllSuggestionRules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllSuggestionRules.fulfilled, (state, action) => {
        state.loading = false;
        state.suggestions = action.payload.results || action.payload;
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(getAllSuggestionRules.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload.message ||
          action.payload.success ||
          action.payload.error ||
          "An Error has occurred";
      })
      .addCase(deleteSuggestionRule.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteSuggestionRule.fulfilled, (state, action) => {
        state.loading = false;
        state.suggestions = state.suggestions.filter(
          (rule) => rule.id !== action.payload
        );
      })
      .addCase(deleteSuggestionRule.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload.message ||
          action.payload.success ||
          action.payload.error ||
          "An Error has occurred";
      })
      .addCase(exportSuggestionRulesCSV.pending, (state) => {
        state.loading = true;
      })
      .addCase(exportSuggestionRulesCSV.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportSuggestionRulesCSV.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message ||
          action.payload?.success ||
          action.payload?.error ||
          "An Error has occurred";
      })
      .addCase(uploadSuggestionRulesCSV.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadSuggestionRulesCSV.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(uploadSuggestionRulesCSV.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message ||
          action.payload?.success ||
          action.payload?.error ||
          "An Error has occurred";
      })
      .addCase(getCriteriaFormats.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCriteriaFormats.fulfilled, (state, action) => {
        state.loading = false;
        // Handle different possible response structures
        const formats =
          action.payload.results || action.payload.data || action.payload || [];
        state.criteriaFormats = Array.isArray(formats) ? formats : [];
      })
      .addCase(getCriteriaFormats.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message ||
          action.payload?.success ||
          action.payload?.error ||
          "An Error has occurred";
      });
  },
});

export default suggestionSlice.reducer;
export const { setLimit, setOffset } = suggestionSlice.actions;
